import { Box, Button, Typography } from '@mui/material';
import { Trash2 } from 'lucide-react';
import { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import { StructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { useStructureSetCommands } from '~/hooks/domain/useStructureSetCommands';
import { useSupportRois } from './useSupportRois';

export interface ExistingSupportStructuresNoticeProps {
    structureSet: StructureSetReadModel | undefined;
}

/**
 * Names the SUPPORT-typed structures already in the set and offers to
 * delete them.
 *
 * Renders nothing when the set holds none, so its presence IS the reason
 * Apply is blocked by the one-couch rule. The structures are named rather
 * than counted, and described as SUPPORT rather than as "the couch",
 * because what is there may be an immobilisation device rather than
 * hardware this tool wrote.
 *
 * Deletion is never a side effect of Apply — always this explicit,
 * confirmed act, per the house rule on irreversible clinical writes.
 */
export default function ExistingSupportStructuresNotice({
    structureSet,
}: ExistingSupportStructuresNoticeProps) {
    const intl = useIntl();
    const { removeRois } = useStructureSetCommands();
    const [confirmOpen, setConfirmOpen] = useState(false);

    const supportRois = useSupportRois(structureSet);
    const names = supportRois.map((r) => r.name).join(', ');

    const handleConfirm = useCallback(() => {
        if (structureSet) {
            removeRois(
                structureSet.id,
                supportRois.map((r) => r.roiId)
            );
        }
        setConfirmOpen(false);
    }, [structureSet, removeRois, supportRois]);

    if (supportRois.length === 0) {
        return null;
    }

    return (
        <Box>
            <Typography
                fontSize={10}
                color="warning.main"
                data-testid="couch-support-warning"
            >
                <FormattedMessage
                    id="couch.existingSupportStructures"
                    values={{ names }}
                />
            </Typography>
            <Button
                variant="outlined"
                color="warning"
                size="small"
                fullWidth
                startIcon={<Trash2 size={14} />}
                onClick={() => setConfirmOpen(true)}
                sx={{ textTransform: 'none', fontSize: 12, mt: 0.5 }}
                data-testid="couch-delete-support"
            >
                <FormattedMessage id="couch.deleteSupportStructures" />
            </Button>
            <ConfirmationDialog
                open={confirmOpen}
                onCancel={() => setConfirmOpen(false)}
                onConfirm={handleConfirm}
                mode="warning"
                title={intl.formatMessage({
                    id: 'couch.deleteSupportStructures',
                })}
                message={intl.formatMessage(
                    { id: 'couch.deleteSupportStructuresMessage' },
                    { names }
                )}
                cancelText={<FormattedMessage id="cancel" />}
                confirmText={<FormattedMessage id="delete" />}
            />
        </Box>
    );
}
